import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Typy ────────────────────────────────────────────────────────────────────
interface BrowserTask {
  task_id: string;
  tool: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  stage?: string;
  progress?: {
    current: number;
    total: number;
    percent: number;
    message: string;
  };
  result?: string;
  error?: string;
}

interface CdpStatus {
  online: boolean;
  title?: string;
  url?: string;
  wsUrl?: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const NOVNC_URL = 'http://localhost:6080/vnc.html?autoconnect=1&resize=scale&show_dot=1&view_only=0';
const BROWSER_TASK_SSE = '/v1/browser-agent/events';  // proxied through backend — no CORS
const AGENT_STATUS_URL = '/v1/browser-agent/status';   // backend proxy — bez CORS

// Stałe używane przez checkCdp / checkMcpHealth przez proxy (unikamy CORS)
// CDP_VERSION_URL i BROWSER_HEALTH wskazują na backend — nie bezpośrednio na kontenery
const CDP_VERSION_URL = AGENT_STATUS_URL;   // alias — checkCdp używa proxy
const BROWSER_HEALTH = AGENT_STATUS_URL;   // alias — checkMcpHealth używa proxy
const API_KEY = () => localStorage.getItem('omnigate_api_key') || '';
const API_BASE = '';

async function apiFetch(path: string, options?: RequestInit) {
  const key = API_KEY();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': key,
      'Authorization': `Bearer ${key}`,
      ...(options?.headers as Record<string, string> || {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ─── Status badge helper ──────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: BrowserTask['status'] }> = ({ status }) => {
  const map: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    running: 'bg-blue-500/20 text-blue-400 animate-pulse',
    completed: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
    cancelled: 'bg-slate-500/20 text-slate-400',
  };
  return (
    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${map[status] ?? 'bg-slate-500/20 text-slate-400'}`}>
      {status}
    </span>
  );
};

// ─── Główny komponent ─────────────────────────────────────────────────────────
const BrowserAgentView: React.FC = () => {
  // Zakładki widoku
  const [viewMode, setViewMode] = useState<'vnc' | 'tasks' | 'run'>('vnc');

  // Stan zadań
  const [tasks, setTasks] = useState<BrowserTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<string | null>(null);

  // Stan połączeń
  const [cdpStatus, setCdpStatus] = useState<CdpStatus>({ online: false });
  const [mcpHealth, setMcpHealth] = useState<{ status: string; running_tasks: number } | null>(null);
  const [sseConnected, setSseConnected] = useState(false);

  // Formularz nowego zadania
  const [taskInput, setTaskInput] = useState('');
  const [taskMode, setTaskMode] = useState<'agent' | 'learn' | 'research'>('agent');
  const [skillName, setSkillName] = useState('');
  const [maxSteps, setMaxSteps] = useState(20);
  const [maxSearches, setMaxSearches] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sseRef = useRef<EventSource | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // ── Sprawdź CDP + MCP — jeden request do proxy backendu (bez CORS) ───────
  const checkStatus = useCallback(async () => {
    try {
      const key = API_KEY();
      const res = await fetch(AGENT_STATUS_URL, {
        signal: AbortSignal.timeout(4000),
        headers: { 'X-API-Key': key, 'Authorization': `Bearer ${key}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // CDP
      const cdp = data.cdp ?? {};
      setCdpStatus({
        online: cdp.online ?? false,
        title: cdp.browser || undefined,
        wsUrl: cdp.ws_url || undefined,
      });

      // MCP
      const mcp = data.mcp ?? {};
      if (mcp.online) {
        setMcpHealth({ status: mcp.status ?? 'ok', running_tasks: mcp.running_tasks ?? 0 });
      } else {
        setMcpHealth(null);
      }
    } catch {
      setCdpStatus({ online: false });
      setMcpHealth(null);
    }
  }, []);

  // Aliasy zachowane dla kompatybilności z useEffect
  const checkCdp = checkStatus;
  const checkMcpHealth = checkStatus;

  // ── SSE — live events ────────────────────────────────────────────────────
  const connectSse = useCallback(() => {
    if (sseRef.current) sseRef.current.close();
    try {
      // EventSource doesn't support custom headers — pass API key as ?token= query param
      // (auth middleware already accepts ?token= for WebSocket/SSE connections)
      const key = API_KEY();
      const sseUrl = key ? `${BROWSER_TASK_SSE}?token=${encodeURIComponent(key)}` : BROWSER_TASK_SSE;
      const source = new EventSource(sseUrl);
      sseRef.current = source;

      source.onopen = () => setSseConnected(true);
      source.onerror = () => {
        setSseConnected(false);
        // Reconnect po 5s
        setTimeout(connectSse, 5000);
      };
      source.onmessage = (e) => {
        try {
          const data: BrowserTask = JSON.parse(e.data);
          setTasks(prev => {
            const idx = prev.findIndex(t => t.task_id === data.task_id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = { ...next[idx], ...data };
              return next;
            }
            return [data, ...prev];
          });
        } catch { /* ignore parse errors */ }
      };
    } catch { /* EventSource not supported */ }
  }, []);

  // ── Pobierz historię zadań ───────────────────────────────────────────────
  const fetchTaskHistory = useCallback(async () => {
    try {
      const data = await apiFetch('/v1/mcp/execute', {
        method: 'POST',
        body: JSON.stringify({
          server: 'browser-use',
          tool: 'task_list',
          arguments: { limit: 20 },
        }),
      });
      const taskList: BrowserTask[] = data?.result?.tasks || data?.tasks || [];
      setTasks(prev => {
        // Merge: SSE dane + historia
        const merged = [...prev];
        taskList.forEach((t: BrowserTask) => {
          const idx = merged.findIndex(m => m.task_id === t.task_id);
          if (idx < 0) merged.push(t);
        });
        return merged.slice(0, 50);
      });
    } catch { /* backend offline */ }
  }, []);

  // ── Uruchom nowe zadanie ─────────────────────────────────────────────────
  const submitTask = useCallback(async () => {
    const trimmed = taskInput.trim();
    if (!trimmed) return;
    if (taskMode === 'learn' && !skillName.trim()) {
      setSubmitError('Podaj nazwę skilla, aby zapisać naukę.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      let tool = taskMode === 'research' ? 'run_deep_research' : 'run_browser_agent';
      let resultPayload: any;

      if (taskMode === 'learn') {
        tool = 'learn';
        const learnRes = await apiFetch('/v1/browser-agent/learn', {
          method: 'POST',
          body: JSON.stringify({
            task: trimmed,
            skill_name: skillName.trim(),
          }),
        });
        resultPayload = learnRes;
      } else {
        const args = taskMode === 'research'
          ? { topic: trimmed, max_searches: maxSearches }
          : { task: trimmed, max_steps: maxSteps };

        const data = await apiFetch('/v1/mcp/execute', {
          method: 'POST',
          body: JSON.stringify({
            server: 'browser-use',
            tool,
            arguments: args,
          }),
        });
        resultPayload = data?.result;
      }

      const taskId = resultPayload?.task_id as string | undefined;

      // Dodaj do listy natychmiast
      const newTask: BrowserTask = {
        task_id: taskId || `tmp-${Date.now()}`,
        tool,
        status: taskId ? 'running' : 'completed',
        progress: taskId
          ? { current: 0, total: taskMode === 'research' ? maxSearches : maxSteps, percent: 0, message: 'Uruchamianie...' }
          : undefined,
        result: !taskId && resultPayload
          ? (typeof resultPayload === 'string' ? resultPayload : JSON.stringify(resultPayload, null, 2))
          : undefined,
      };
      setTasks(prev => [newTask, ...prev]);
      setTaskInput('');
      setViewMode(taskMode === 'research' ? 'tasks' : 'vnc');  // Research nie potrzebuje VNC
    } catch (err: any) {
      setSubmitError(err.message || 'Błąd uruchamiania zadania');
    } finally {
      setIsSubmitting(false);
    }
  }, [taskInput, taskMode, skillName, maxSteps, maxSearches]);

  // ── Anuluj zadanie ───────────────────────────────────────────────────────
  const cancelTask = useCallback(async (taskId: string) => {
    try {
      await apiFetch('/v1/mcp/execute', {
        method: 'POST',
        body: JSON.stringify({
          server: 'browser-use',
          tool: 'task_cancel',
          arguments: { task_id: taskId },
        }),
      });
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, status: 'cancelled' } : t));
    } catch { /* ignore */ }
  }, []);

  // ── Lifecycle ────────────────────────────────────────────────────────────
  useEffect(() => {
    checkStatus();
    connectSse();
    fetchTaskHistory();

    const statusInterval = setInterval(checkStatus, 8000);   // CDP + MCP razem
    const histInterval = setInterval(fetchTaskHistory, 15000);

    return () => {
      clearInterval(statusInterval);
      clearInterval(histInterval);
      sseRef.current?.close();
    };
  }, [checkStatus, connectSse, fetchTaskHistory]);

  const runningTasks = tasks.filter(t => t.status === 'running' || t.status === 'pending');
  const selectedTaskData = tasks.find(t => t.task_id === selectedTask);

  return (
    <div className="flex flex-col h-full bg-[#131722] text-[#d1d4dc] font-mono overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-b border-[#2a2e39] px-4 py-3 bg-[#0d0f14]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-cyan-600/30 rounded-lg flex items-center justify-center border border-cyan-500/30">
              <i className="fas fa-globe text-cyan-400 text-sm"></i>
            </div>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-tighter">
                Browser Agent <span className="text-cyan-400">Live View</span>
              </h2>
              <p className="text-[9px] text-slate-500 uppercase tracking-widest">
                CDP :8225 → Playwright → noVNC :6080
              </p>
            </div>
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-2">
            {/* CDP status */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${cdpStatus.online
                ? 'bg-green-500/10 text-green-400 border-green-500/30'
                : 'bg-red-500/10 text-red-400 border-red-500/30'
              }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${cdpStatus.online ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
              CDP {cdpStatus.online ? 'online' : 'offline'}
            </div>

            {/* MCP health */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${mcpHealth
                ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                : 'bg-slate-500/10 text-slate-500 border-slate-500/30'
              }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${mcpHealth ? 'bg-blue-400 animate-pulse' : 'bg-slate-500'}`} />
              MCP {mcpHealth ? `${mcpHealth.running_tasks} running` : 'offline'}
            </div>

            {/* SSE status */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${sseConnected
                ? 'bg-purple-500/10 text-purple-400 border-purple-500/30'
                : 'bg-slate-500/10 text-slate-500 border-slate-500/30'
              }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${sseConnected ? 'bg-purple-400 animate-pulse' : 'bg-slate-500'}`} />
              SSE {sseConnected ? 'live' : 'off'}
            </div>

            {/* Running count badge */}
            {runningTasks.length > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 animate-pulse">
                <i className="fas fa-spinner fa-spin text-[8px]" />
                {runningTasks.length} task{runningTasks.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 mt-3">
          {[
            { id: 'vnc', icon: 'fa-desktop', label: 'Live View (noVNC)' },
            { id: 'run', icon: 'fa-play', label: 'Uruchom zadanie' },
            { id: 'tasks', icon: 'fa-list', label: `Historia (${tasks.length})` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setViewMode(tab.id as any)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-black uppercase transition-all ${viewMode === tab.id
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-[#1e222d] border border-transparent'
                }`}
            >
              <i className={`fas ${tab.icon} text-[9px]`} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden flex gap-0">

        {/* Główny panel */}
        <div className="flex-1 overflow-hidden flex flex-col">

          {/* ══ VNC View ══ */}
          {viewMode === 'vnc' && (
            <div className="flex-1 relative bg-black overflow-hidden">
              {cdpStatus.online ? (
                <>
                  <iframe
                    ref={iframeRef}
                    src={NOVNC_URL}
                    className="w-full h-full border-0"
                    title="Browser Agent Live View"
                    allow="clipboard-read; clipboard-write"
                  />
                  {/* Overlay — aktywne zadania */}
                  {runningTasks.length > 0 && (
                    <div className="absolute top-3 left-3 space-y-2 pointer-events-none z-10">
                      {runningTasks.map(t => (
                        <div key={t.task_id} className="bg-black/80 backdrop-blur-sm border border-blue-500/40 rounded-lg p-2.5 max-w-xs">
                          <div className="flex items-center gap-2 mb-1">
                            <i className="fas fa-spinner fa-spin text-blue-400 text-[10px]" />
                            <span className="text-[9px] text-blue-400 font-black uppercase">{t.stage || 'running'}</span>
                          </div>
                          {t.progress && (
                            <>
                              <div className="w-full bg-[#0d0f14] rounded-full h-1 mb-1">
                                <div
                                  className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                                  style={{ width: `${t.progress.percent || 0}%` }}
                                />
                              </div>
                              <p className="text-[9px] text-slate-300 truncate">{t.progress.message}</p>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center h-full gap-4 text-slate-500">
                  <div className="w-16 h-16 rounded-full bg-[#1e222d] border border-[#2a2e39] flex items-center justify-center">
                    <i className="fas fa-desktop text-2xl text-slate-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-black uppercase text-slate-400 mb-1">Kontener nie uruchomiony</p>
                    <p className="text-xs text-slate-500 mb-3">Uruchom kontener żeby zobaczyć podgląd przeglądarki</p>
                    <code className="text-[10px] bg-[#1e222d] px-3 py-2 rounded border border-[#2a2e39] text-cyan-400 block max-w-sm mx-auto">
                      cd ~/ai-gatewav-v2 && docker compose -f docker-compose-agent-browser.yml up -d
                    </code>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[9px] mt-2">
                    <div className="bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-center">
                      <p className="text-slate-500">CDP</p>
                      <p className="text-slate-400 font-mono">:8225</p>
                    </div>
                    <div className="bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-center">
                      <p className="text-slate-500">noVNC</p>
                      <p className="text-slate-400 font-mono">:6080</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ Run Task ══ */}
          {viewMode === 'run' && (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-2xl mx-auto space-y-5">
                <div>
                  <h3 className="text-sm font-black text-white uppercase mb-1">Nowe zadanie przeglądarki</h3>
                  <p className="text-[10px] text-slate-500">
                    Agent otworzy przeglądarkę w kontenerze. Podgląd na żywo dostępny w zakładce "Live View".
                  </p>
                </div>

                {/* Mode selector */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400">Tryb zadania</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'agent', label: 'Agent', icon: 'fa-robot', desc: 'Standardowe wykonanie' },
                      { id: 'learn', label: 'Learn', icon: 'fa-brain', desc: 'Nauka skilla (API discovery)' },
                      { id: 'research', label: 'Research', icon: 'fa-search', desc: 'Raport z badań' },
                    ].map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => setTaskMode(mode.id as any)}
                        className={`px-3 py-2 rounded-lg border text-[10px] font-black uppercase transition-all ${taskMode === mode.id
                          ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                          : 'bg-[#1e222d] text-slate-400 border-[#2a2e39] hover:text-slate-200'
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          <i className={`fas ${mode.icon} text-[9px]`} />
                          <span>{mode.label}</span>
                        </div>
                        <p className="text-[9px] font-normal mt-0.5 normal-case text-slate-500">{mode.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Skill name (learn mode) */}
                {taskMode === 'learn' && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400">Nazwa skilla</label>
                    <input
                      value={skillName}
                      onChange={e => setSkillName(e.target.value)}
                      placeholder="np. github_repo_search"
                      className="w-full bg-[#1e222d] border border-[#2a2e39] rounded-lg px-3 py-2 text-xs text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none font-mono"
                    />
                    <p className="text-[9px] text-slate-600">Wymagane do zapisu wyuczonego skilla.</p>
                  </div>
                )}

                {/* CDP warning */}
                {!cdpStatus.online && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-[10px] text-yellow-400 flex items-start gap-2">
                    <i className="fas fa-exclamation-triangle mt-0.5" />
                    <div>
                      <p className="font-black uppercase mb-0.5">Kontener nie uruchomiony</p>
                      <p>Zadanie zostanie wysłane do mcp-server-browser-use, który uruchomi własny Chromium na hoście.</p>
                    </div>
                  </div>
                )}

                {/* Task input */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400">
                    {taskMode === 'research' ? 'Temat badania' : 'Opis zadania'}
                  </label>
                  <textarea
                    value={taskInput}
                    onChange={e => setTaskInput(e.target.value)}
                    placeholder={taskMode === 'research'
                      ? 'np. Jakie są najnowsze trendy w automatyzacji MCP?'
                      : 'np. Przejdź na github.com i wyszukaj "mcp browser automation". Zwróć pierwsze 3 wyniki.'}
                    rows={5}
                    className="w-full bg-[#1e222d] border border-[#2a2e39] rounded-lg p-3 text-sm text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none resize-none font-mono"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitTask();
                    }}
                  />
                  <p className="text-[9px] text-slate-600">Ctrl+Enter aby uruchomić</p>
                </div>

                {/* Max steps / searches */}
                {taskMode !== 'research' ? (
                  <div className="flex items-center gap-4">
                    <label className="text-[10px] font-black uppercase text-slate-400 whitespace-nowrap">
                      Max kroków: <span className="text-cyan-400">{maxSteps}</span>
                    </label>
                    <input
                      type="range"
                      min={5} max={50} step={5}
                      value={maxSteps}
                      onChange={e => setMaxSteps(Number(e.target.value))}
                      className="flex-1 accent-cyan-500"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <label className="text-[10px] font-black uppercase text-slate-400 whitespace-nowrap">
                      Max wyszukiwań: <span className="text-cyan-400">{maxSearches}</span>
                    </label>
                    <input
                      type="range"
                      min={2} max={12} step={1}
                      value={maxSearches}
                      onChange={e => setMaxSearches(Number(e.target.value))}
                      className="flex-1 accent-cyan-500"
                    />
                  </div>
                )}

                {/* Error */}
                {submitError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-[10px] text-red-400 flex items-center gap-2">
                    <i className="fas fa-times-circle" />
                    {submitError}
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={submitTask}
                  disabled={isSubmitting || !taskInput.trim() || (taskMode === 'learn' && !skillName.trim())}
                  className="w-full py-3 bg-cyan-600/80 hover:bg-cyan-500/80 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-sm uppercase rounded-lg border border-cyan-500/30 transition-all flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <><i className="fas fa-spinner fa-spin" /> Uruchamianie...</>
                  ) : (
                    <><i className="fas fa-play" /> {taskMode === 'research' ? 'Uruchom research' : taskMode === 'learn' ? 'Uruchom learning' : 'Uruchom zadanie w przeglądarce'}</>
                  )}
                </button>

                {/* Quick templates */}
                <div className="space-y-2">
                  <p className="text-[9px] font-black uppercase text-slate-500">Szybkie szablony</p>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { label: 'Wyszukaj w Google', task: 'Wyszukaj w Google: "' },
                      { label: 'Otwórz URL i opisz', task: 'Otwórz stronę i opisz jej zawartość: ' },
                      { label: 'Sprawdź status serwisu', task: 'Wejdź na stronę i sprawdź czy działa poprawnie: ' },
                      { label: 'Zrób screenshot i opisz', task: 'Wejdź na stronę, zrób screenshot i opisz co widzisz: ' },
                    ].map(tpl => (
                      <button
                        key={tpl.label}
                        onClick={() => setTaskInput(tpl.task)}
                        className="text-left px-3 py-2 bg-[#1e222d] hover:bg-[#2a2e39] border border-[#2a2e39] rounded text-[10px] text-slate-400 hover:text-slate-200 transition-colors"
                      >
                        <i className="fas fa-bolt text-yellow-500 mr-2 text-[8px]" />
                        {tpl.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══ Task History ══ */}
          {viewMode === 'tasks' && (
            <div className="flex-1 overflow-hidden flex">
              {/* Lista zadań */}
              <div className="w-80 flex-shrink-0 border-r border-[#2a2e39] overflow-y-auto">
                <div className="p-3 border-b border-[#2a2e39] flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase text-slate-400">Zadania ({tasks.length})</p>
                  <button
                    onClick={fetchTaskHistory}
                    className="text-[9px] text-slate-500 hover:text-slate-300 px-2 py-1 rounded hover:bg-[#1e222d] transition-colors"
                  >
                    <i className="fas fa-sync-alt" />
                  </button>
                </div>
                {tasks.length === 0 ? (
                  <div className="p-4 text-center text-slate-600 text-[10px]">
                    <i className="fas fa-inbox text-2xl mb-2 block" />
                    Brak zadań
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {tasks.map(t => (
                      <button
                        key={t.task_id}
                        onClick={() => setSelectedTask(t.task_id === selectedTask ? null : t.task_id)}
                        className={`w-full text-left p-2.5 rounded-lg border transition-all ${selectedTask === t.task_id
                            ? 'bg-cyan-500/10 border-cyan-500/30'
                            : 'bg-[#1e222d] border-[#2a2e39] hover:border-slate-500/50'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] font-mono text-slate-500 truncate">{t.task_id.slice(0, 8)}…</span>
                          <StatusBadge status={t.status} />
                        </div>
                        <p className="text-[10px] text-slate-300 font-mono truncate">{t.tool}</p>
                        {t.progress && (
                          <div className="mt-1.5 w-full bg-[#0d0f14] rounded-full h-0.5">
                            <div
                              className={`h-0.5 rounded-full transition-all ${t.status === 'completed' ? 'bg-green-500' :
                                  t.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                                }`}
                              style={{ width: `${t.progress.percent || 0}%` }}
                            />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Detail pane */}
              <div className="flex-1 overflow-y-auto p-4">
                {!selectedTaskData ? (
                  <div className="h-full flex items-center justify-center text-slate-600 text-[10px] text-center">
                    <div>
                      <i className="fas fa-mouse-pointer text-2xl mb-2 block" />
                      Wybierz zadanie z listy
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[9px] font-black uppercase text-slate-500 mb-1">Task ID</p>
                        <code className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">
                          {selectedTaskData.task_id}
                        </code>
                      </div>
                      <div className="flex gap-2">
                        {(selectedTaskData.status === 'running' || selectedTaskData.status === 'pending') && (
                          <button
                            onClick={() => cancelTask(selectedTaskData.task_id)}
                            className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-[10px] font-black uppercase rounded border border-red-500/30 transition-colors"
                          >
                            <i className="fas fa-stop mr-1" /> Anuluj
                          </button>
                        )}
                        <StatusBadge status={selectedTaskData.status} />
                      </div>
                    </div>

                    {/* Progress */}
                    {selectedTaskData.progress && (
                      <div className="bg-[#1e222d] rounded-lg border border-[#2a2e39] p-3 space-y-2">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-slate-400 uppercase font-black">Postęp</span>
                          <span className="text-cyan-400 font-black">
                            {selectedTaskData.progress.current}/{selectedTaskData.progress.total} ({selectedTaskData.progress.percent?.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="w-full bg-[#0d0f14] rounded-full h-2">
                          <div
                            className="bg-cyan-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${selectedTaskData.progress.percent || 0}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-300 font-mono">
                          {selectedTaskData.progress.message}
                        </p>
                      </div>
                    )}

                    {/* Stage */}
                    {selectedTaskData.stage && (
                      <div className="bg-[#1e222d] rounded-lg border border-[#2a2e39] p-3">
                        <p className="text-[9px] uppercase font-black text-slate-500 mb-1">Stage</p>
                        <p className="text-[11px] text-white font-mono">{selectedTaskData.stage}</p>
                      </div>
                    )}

                    {/* Result */}
                    {selectedTaskData.result && (
                      <div className="bg-[#1e222d] rounded-lg border border-green-500/20 p-3">
                        <p className="text-[9px] uppercase font-black text-green-400 mb-2 flex items-center gap-1">
                          <i className="fas fa-check-circle" /> Wynik
                        </p>
                        <pre className="text-[10px] text-slate-300 font-mono whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">
                          {selectedTaskData.result}
                        </pre>
                      </div>
                    )}

                    {/* Error */}
                    {selectedTaskData.error && (
                      <div className="bg-[#1e222d] rounded-lg border border-red-500/20 p-3">
                        <p className="text-[9px] uppercase font-black text-red-400 mb-2 flex items-center gap-1">
                          <i className="fas fa-times-circle" /> Błąd
                        </p>
                        <pre className="text-[10px] text-red-300 font-mono whitespace-pre-wrap">
                          {selectedTaskData.error}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer status bar ───────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-[#2a2e39] px-4 py-1.5 bg-[#0d0f14] flex items-center justify-between text-[9px] font-mono">
        <div className="flex items-center gap-4 text-slate-500">
          <span>
            <span className="text-slate-400">noVNC:</span> localhost:6080
          </span>
          <span>
            <span className="text-slate-400">CDP:</span> localhost:8225
          </span>
          <span>
            <span className="text-slate-400">MCP:</span> localhost:8383
          </span>
          {cdpStatus.title && (
            <span className="text-green-500">
              <i className="fas fa-check-circle mr-1" />{cdpStatus.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-slate-500">
          <span>{tasks.filter(t => t.status === 'completed').length} ukończone</span>
          <span>{tasks.filter(t => t.status === 'failed').length} błędy</span>
          <span className={runningTasks.length > 0 ? 'text-blue-400 animate-pulse' : ''}>
            {runningTasks.length} aktywne
          </span>
        </div>
      </div>
    </div>
  );
};

export default BrowserAgentView;
